import { Link } from 'react-router';
import { ArrowLeft, Shield, Lock, Eye, Server, FileCheck, Users } from 'lucide-react';
import { PublicFooter } from '@/components/PublicFooter';
import { usePageMeta } from '@/hooks/usePageMeta';

const securityItems = [
  {
    icon: Lock,
    title: 'Encryption',
    description:
      "All data is encrypted at rest using AES-256 and in transit using TLS 1.3. Database backups are also encrypted. We use Vercel's edge-optimized infrastructure with automatic SSL certificate management.",
  },
  {
    icon: Shield,
    title: 'Access Controls',
    description:
      'Access to production systems is restricted to essential personnel using role-based access control (RBAC). All administrative access requires multi-factor authentication and is logged, monitored, and audited on a continuous basis.',
  },
  {
    icon: Eye,
    title: 'Monitoring & Detection',
    description:
      'We operate 24/7 automated monitoring for unauthorized access attempts, anomalous API patterns, and potential data exfiltration. Alerts are triaged by our security team with defined escalation paths.',
  },
  {
    icon: FileCheck,
    title: 'Third-Party Audits',
    description:
      'Our infrastructure and practices undergo regular third-party security assessments. We conduct annual penetration tests and vulnerability scans against our application and API surfaces.',
  },
  {
    icon: Server,
    title: 'Infrastructure Security',
    description:
      "For Visibility runs on Vercel's SOC 2-compliant infrastructure. We enforce network segmentation, regular patch management, and immutable deployments — every deploy is a fresh, verified build with no drift.",
  },
  {
    icon: Users,
    title: 'Employee Practices',
    description:
      'All employees undergo annual security awareness training. Access to production data is granted on a need-to-know basis and revoked immediately upon role change or departure. We conduct background checks on security-sensitive roles.',
  },
];

export default function Security() {
  usePageMeta({
    title: 'Security & Vulnerability Disclosure',
    description:
      'How For Visibility protects your data — encryption, RLS tenant isolation, SOC 2 infrastructure, and our vulnerability disclosure policy.',
    canonicalPath: '/legal/security',
    ogPage: 'security',
  });
  return (
    <div className="min-h-screen bg-fos-bg-base">
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-1 text-sm text-fos-text-muted transition-colors hover:text-fos-text-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <article className="prose prose-gray max-w-none">
          <h1>Security</h1>
          <p className="lead">
            Your data security is integral to For Visibility. We build with security as a
            foundational requirement, not an afterthought.
          </p>

          <p>
            We understand that you're trusting us with your business data — marketplace credentials,
            inventory records, pricing intelligence, and financial information. This page describes
            the practices, technologies, and policies we use to protect that trust.
          </p>
        </article>

        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          {securityItems.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                className="rounded-lg border border-fos-border bg-fos-bg-card p-6"
              >
                <Icon className="mb-3 h-6 w-6 text-fos-accent" />
                <h3 className="mb-2 font-semibold text-fos-text-primary">{item.title}</h3>
                <p className="text-sm leading-relaxed text-fos-text-muted">{item.description}</p>
              </div>
            );
          })}
        </div>

        <article className="prose prose-gray mt-12 max-w-none">
          <h2>Vulnerability Disclosure</h2>
          <p>
            If you believe you've found a security vulnerability in For Visibility, please disclose
            it responsibly. Contact our security team at{' '}
            <a href="mailto:security@forvisibility.com">security@forvisibility.com</a> with details
            about the issue. We commit to acknowledging your report within 48 hours and providing a
            timeline for resolution.
          </p>

          <h2>Compliance & Certifications</h2>
          <p>
            For Visibility operates on Vercel's SOC 2-compliant infrastructure. Our marketplace
            integrations follow each platform's security requirements, and we undergo regular
            independent security reviews. We are committed to achieving SOC 2 Type II certification.
          </p>

          <h2>Questions?</h2>
          <p>
            If you have questions about our security practices, contact our security team at{' '}
            <a href="mailto:security@forvisibility.com">security@forvisibility.com</a>.
          </p>
        </article>
      </div>
      <PublicFooter />
    </div>
  );
}
